/**
 * APX Hub — Centralized Role Guard Utility
 *
 * Single source of truth for role-based access control across all pages.
 * Use these helpers instead of inline role checks to ensure consistency.
 *
 * Role hierarchy (highest → lowest):
 *   super_admin > admin > controller > bookkeeping_manager > bookkeeper_supervisor > tax_division > bookkeeper
 */

export type AppRole =
  | "super_admin"
  | "admin"
  | "controller"
  | "asst_controller"
  | "bookkeeping_manager"
  | "bookkeeper_supervisor"
  | "tax_division"
  | "bookkeeper";

/** Roles that have full admin-level access (Mr. B + any future admins) */
export const ADMIN_ROLES: AppRole[] = ["super_admin", "admin"];

/** Roles that can manage staff and view all client data */
export const MANAGER_ROLES: AppRole[] = ["super_admin", "admin", "controller", "bookkeeping_manager"];

/** Bookkeeping roles — supervisor + regular bookkeepers */
export const BOOKKEEPER_ROLES: AppRole[] = ["bookkeeper_supervisor", "bookkeeper"];

/** All roles that can log in */
export const ALL_ROLES: AppRole[] = [
  "super_admin",
  "admin",
  "controller",
  "asst_controller",
  "bookkeeping_manager",
  "bookkeeper_supervisor",
  "tax_division",
  "bookkeeper",
];

// ─── Role normalization ──────────────────────────────────────────────────────

/**
 * Normalizes a role for nav/UI filtering purposes.
 * "admin" is treated identically to "super_admin" throughout the UI.
 * Use this instead of scattering role === "super_admin" || role === "admin" checks.
 *
 * WHY THIS EXISTS: The DB stores Mr. B as role="admin" but the nav items were
 * originally written with roles:["super_admin"]. Adding this normalization at
 * one place prevents the blank-sidebar bug from ever recurring.
 */
export function normalizeRole(role: AppRole | string | null | undefined): AppRole {
  if (role === "admin") return "super_admin";
  if (ALL_ROLES.includes(role as AppRole)) return role as AppRole;
  return "bookkeeper"; // safe fallback for unknown roles
}

// ─── Guard functions ──────────────────────────────────────────────────────────

/** Returns true if the user has super_admin or admin role */
export function isAdmin(role?: string | null): boolean {
  return ADMIN_ROLES.includes(role as AppRole);
}

/** Returns true if the user is specifically super_admin (Mr. B only) */
export function isSuperAdmin(role?: string | null): boolean {
  return role === "super_admin";
}

/** Returns true if the user can manage staff and view all clients */
export function isManager(role?: string | null): boolean {
  return MANAGER_ROLES.includes(role as AppRole);
}

/** Returns true if the user is a bookkeeper or bookkeeper supervisor */
export function isBookkeeper(role?: string | null): boolean {
  return BOOKKEEPER_ROLES.includes(role as AppRole);
}

/** Returns true if the user has any valid app role */
export function isValidRole(role?: string | null): boolean {
  return ALL_ROLES.includes(role as AppRole);
}

/** Returns true if the user can access the Admin Settings page */
export function canAccessSettings(role?: string | null): boolean {
  return isAdmin(role);
}

/** Returns true if the user can access Team & Access management */
export function canAccessTeamManagement(role?: string | null): boolean {
  return isManager(role);
}

/** Returns true if the user can access the Google Drive docs page */
export function canAccessDrive(role?: string | null): boolean {
  return isValidRole(role);
}

/** Returns true if the user can access the Email Inbox */
export function canAccessEmail(role?: string | null): boolean {
  return isManager(role);
}

/** Returns true if the user can trigger manual Gmail sync */
export function canTriggerSync(role?: string | null): boolean {
  return isAdmin(role);
}

/** Returns true if the user can view the Connection Protocol settings */
export function canAccessConnectionSettings(role?: string | null): boolean {
  return isSuperAdmin(role);
}

// ─── Route guard helper ───────────────────────────────────────────────────────

/**
 * Returns the redirect path if the user should NOT access this page,
 * or null if access is allowed.
 */
export function getRedirectIfUnauthorized(
  role: string | null | undefined,
  requiredLevel: "super_admin" | "admin" | "manager" | "any"
): string | null {
  if (!role) return "/login";

  switch (requiredLevel) {
    case "super_admin":
      return isSuperAdmin(role) ? null : "/dashboard";
    case "admin":
      return isAdmin(role) ? null : "/dashboard";
    case "manager":
      return isManager(role) ? null : "/dashboard";
    case "any":
      return isValidRole(role) ? null : "/login";
    default:
      return "/dashboard";
  }
}
